import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import {
  MathStage,
  GraphStage,
  Graph3DStage,
  PhysicsStage,
  ChemistryStage,
  CollaborationStage,
  CinematicStage,
} from './AnimationStages';

export const WhiteboardCanvas = ({
  activeSection,
  mode = 'desktop',
  onMobileSectionChange,
  disableTouchSwipe = false,
}) => {
  const screenRef = useRef(null);
  const [scale, setScale] = useState(1);
  const touchStartXRef = useRef(null);
  const touchEndXRef = useRef(null);

  useEffect(() => {
    const el = screenRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      if (width > 0) {
        const fittedScale = width / DESIGN_WIDTH;
        // Keep a little breathing room on smaller viewports so animated stages
        // don't visually collide with the panel edges.
        setScale(mode === 'mobile' ? fittedScale * 0.9 : fittedScale);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [mode]);

  const renderStageForIndex = useCallback((idx) => {
    switch (idx) {
      case 0:
        return <InitialStage />;
      case 1:
        return <MathStage isActive={true} />;
      case 2:
        return <GraphStage isActive={true} />;
      case 3:
        return <PhysicsStage isActive={true} />;
      case 4:
        return <ChemistryStage isActive={true} />;
      case 5:
        return <CollaborationStage isActive={true} />;
      case 6:
        return <CinematicStage isActive={true} />;
      default:
        return null;
    }
  }, []);

  const handleTouchStart = (event) => {
    if (mode !== 'mobile' || disableTouchSwipe) return;
    touchStartXRef.current = event.touches[0].clientX;
    touchEndXRef.current = null;
  };

  const handleTouchMove = (event) => {
    if (mode !== 'mobile' || disableTouchSwipe) return;
    touchEndXRef.current = event.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (mode !== 'mobile' || disableTouchSwipe) return;
    if (touchStartXRef.current == null || touchEndXRef.current == null) return;

    const deltaX = touchStartXRef.current - touchEndXRef.current;
    const swipeThreshold = 4

    if (deltaX > swipeThreshold) {
      onMobileSectionChange?.(activeSection + 1);
    } else if (deltaX < -swipeThreshold) {
      onMobileSectionChange?.(activeSection - 1);
    }

    touchStartXRef.current = null;
    touchEndXRef.current = null;
  };

  return (
    <div className="w-full max-w-full relative overflow-hidden">
      {/* IFPD Panel */}
      <div
        className="relative w-full aspect-[16/10] overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, #525252 0%, #3d3d3d 40%, #454545 100%)',
          borderRadius: '14px',
          boxShadow: '0 25px 60px rgba(0,0,0,0.25), 0 8px 20px rgba(0,0,0,0.12)',
        }}
        data-testid="whiteboard-canvas"
      >
        {/* Subtle top highlight on bezel */}
        <div
          className="absolute inset-0 pointer-events-none z-20"
          style={{
            borderRadius: '14px',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, transparent 30%)',
          }}
        />
        {/* Small indicator dot — bottom right of bezel */}
        <div className="absolute bottom-[6px] right-[18px] z-30 w-[5px] h-[5px] rounded-full bg-slate-400/30" />
        
        {/* Screen area — white canvas with inset creating the bezel */}
        <div
          ref={screenRef}
          className="absolute bg-white overflow-hidden z-10"
          style={{ inset: '14px', borderRadius: '3px' }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Dotted canvas background */}
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: 'radial-gradient(circle, #94A3B8 1px, transparent 1px)',
              backgroundSize: '20px 20px',
            }}
          />
          
          {/* Scaled animation container */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`${mode}-${activeSection}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <div
                style={{
                  width: DESIGN_WIDTH,
                  height: DESIGN_WIDTH * 0.625,
                  transform: `scale(${scale})`,
                  transformOrigin: 'center center',
                }}
                className="relative max-w-full"
              >
                {renderStageForIndex(activeSection)}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {mode !== 'mobile' && (
        /* IFPD Stand — absolutely positioned, overlaps panel bottom to connect seamlessly */
        <div className="absolute top-full left-1/2 -translate-x-1/2 w-[80%] -z-10" style={{ marginTop: '-8px' }}>
          <svg
            viewBox="0 0 400 208"
            className="w-full"
            preserveAspectRatio="xMidYMin meet"
          >
            <defs>
              <linearGradient id="legGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#4a4a4a" />
                <stop offset="40%" stopColor="#585858" />
                <stop offset="100%" stopColor="#404040" />
              </linearGradient>
              <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#565656" />
                <stop offset="100%" stopColor="#404040" />
              </linearGradient>
            </defs>

            {/* Left leg */}
            <rect x="76" y="-10" width="12" height="180" rx="3" fill="url(#legGrad)" />
            {/* Right leg */}
            <rect x="312" y="-10" width="12" height="180" rx="3" fill="url(#legGrad)" />

            {/* Horizontal crossbar — at 2/3 from bottom (legs span 0 to 170, so 2/3 from bottom = 170 * 1/3 ≈ 57) */}
            <rect x="82" y="57" width="236" height="9" rx="4.5" fill="url(#barGrad)" />

            {/* Left caster wheel */}
            <circle cx="82" cy="180" r="9" fill="#3d3d3d" />
            <circle cx="82" cy="180" r="5.5" fill="#4a4a4a" />
            <circle cx="82" cy="180" r="2" fill="#363636" />

            {/* Right caster wheel */}
            <circle cx="318" cy="180" r="9" fill="#3d3d3d" />
            <circle cx="318" cy="180" r="5.5" fill="#4a4a4a" />
            <circle cx="318" cy="180" r="2" fill="#363636" />
          </svg>
        </div>
      )}
    </div>
  );
};

const DESIGN_WIDTH = 400; // Base design width for all animations

// Initial "Hello Class" stage — handwriting animation
const InitialStage = () => {
  const line1 = "Hello, Class!";
  const line2 = "Let's explore together";
  const charDelay = 0.07;
  const line2Start = line1.length * charDelay + 0.3;

  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center"
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
    >
      <div className="text-center px-4">
        <div className="font-handwritten text-5xl text-brand">
          {line1.split("").map((char, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * charDelay, duration: 0.15, ease: "easeOut" }}
            >
              {char}
            </motion.span>
          ))}
        </div>
        <div className="mt-4 font-handwritten text-2xl text-slate-400">
          {line2.split("").map((char, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: line2Start + i * charDelay, duration: 0.12, ease: "easeOut" }}
            >
              {char}
            </motion.span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default WhiteboardCanvas;

